import type { ContentHref, RequiresRelation } from '@generalTypes/apiTypes';
import {
  AsyncKovResourcePicker,
  KovResourcePicker,
  Modal,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import { referenceFrameLabels, referenceFrames } from '@newStore/referenceFrames/referenceFrames';
import type { EditAsideReferenceFrameReferences } from '@nodeTypeConfig/configTypes';
import { contentApi } from '@store/api/apiConfig';
import { relationStrengths } from '@store/constants/relationStrengths';
import React, { useState } from 'react';

export interface LlinkidReferenceModalProps {
  config?: EditAsideReferenceFrameReferences;
  edit?: {
    relation: RequiresRelation;
    item: { $$meta: { permalink: ContentHref } };
  };
  alreadySelectedReferences?: Array<ContentHref>;
  onClose?: () => void;
  onSubmit?: (response: { $$meta: { permalink: ContentHref }; strength: string }) => void;
}

const LlinkidReferenceModal: React.FC<LlinkidReferenceModalProps> = ({
  config,
  edit,
  alreadySelectedReferences = [],
  onClose = () => {},
  onSubmit,
}) => {
  const [strength, setStrength] = useState(
    relationStrengths.find((rel) => rel.key === (edit?.relation?.strength || 'MEDIUM')),
  );
  const [selectedReference, setSelectedReference] = useState(edit?.item || null);
  const isEducationalComponent =
    config?.options.referenceFrame === referenceFrames.educationalComponents;

  const sriLoadOptionsFn = useLoadSriOptions({
    sriClient: contentApi,
    href: '/content',
    parameters: {
      type: 'THEME',
      root: config?.options.referenceFrame,
      orderBy: 'title',
    },
  });

  const onChangeReference = (e) => {
    setSelectedReference(e.$$expanded);
  };

  const onChangeStrength = (value: { key: string; label: string }) => {
    setStrength(value);
  };

  const handleSubmit = () => {
    if (onSubmit && selectedReference && strength) {
      onSubmit({
        $$meta: { permalink: selectedReference.$$meta.permalink },
        strength: strength.key,
      });
    }
    onClose();
  };

  const filterData = ({ data }: { data: { href: ContentHref } }) => {
    return !alreadySelectedReferences.includes(data.href);
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title={`${config?.label} invoegen`} onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <AsyncKovResourcePicker
                defaultValue={selectedReference}
                onChange={onChangeReference}
                loadOptions={sriLoadOptionsFn}
                filter={filterData}
                localData={true}
                optionValue={(option) => option.href}
                placeholder={referenceFrameLabels[config?.options?.referenceFrame || '']}
                clearable={false}
                multiple={false}
                optionTemplate={(option) => (
                  <div className="picker-result author">
                    <strong className="title" title={option.$$expanded?.title || option.title}>
                      {option.$$expanded?.title || option.title}
                    </strong>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        {isEducationalComponent && (
          <div className="form-group">
            <div className="row">
              <div className="col-md-12">
                <>
                  <label className="control-label">
                    Hoe sterk is deze component aanwezig in het leerplan?
                  </label>
                  <KovResourcePicker
                    defaultValue={strength}
                    localData={relationStrengths}
                    display={(o) => o.label}
                    clearable={true}
                    disableSearch={true}
                    menuPlacement="bottom"
                    onChange={onChangeStrength}
                  />
                </>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!selectedReference || !strength}
        >
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

LlinkidReferenceModal.displayName = 'LlinkidReferenceModal';

export default LlinkidReferenceModal;
